import { Children, type ComponentProps } from 'react'

import type { DropdownProps } from 'react-day-picker'
import { DayPicker } from 'react-day-picker'

// import IconChevronDown from "@/icons/icon-chevron-down.svg";
import type { SelectOptionsType } from '../form/FormSelect.tsx'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../select'

export type CalendarProps = ComponentProps<typeof DayPicker>

/**
 * @param mode The mode that indicates the select method. Possible values: "single", "range", "multiple".
 * @param selected The currently selected date(s) state.
 * @param onSelect A function to set the selected date(s).
 * @param numberOfMonths The number of months to display.
 *
 * @example
 * // Initializing state for a date range selection
 * const [date, setDate] = useState<DateRange | undefined>({
 *   from: new Date(2022, 0, 20),
 *   to: addDays(new Date(2022, 0, 20), 20),
 * });
 *
 * // Using the Calendar component for a date range selection
 * return <Calendar mode="range" selected={date} onSelect={setDate} />
 */
export const Calendar = ({
  showOutsideDays = true,
  className,
  classNames,
  ...props
}: CalendarProps) => {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className="bg-card text-title rounded-md"
      components={{ Dropdown }}
      classNames={{
        months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
        month: 'space-y-4',
        caption: 'flex justify-center pt-1 relative items-center ',
        caption_label: 'text-sm font-semibold',
        caption_dropdowns: 'flex items-center w-full gap-2',
        nav: 'space-x-1 flex items-center',
        nav_button_previous: 'absolute left-1 border p-2 rounded-md text-title',
        nav_button_next: 'absolute right-1 border p-2 rounded-md text-title',
        table: 'w-full border-collapse space-y-1',
        head_row: 'flex',
        head_cell:
          'text-secondary-200 rounded-md w-9 font-normal text-[0.8rem]',
        row: 'flex w-full mt-2',
        cell: 'text-center text-sm p-0 relative first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20',
        day: 'h-9 w-9 p-0 font-normal aria-selected:opacity-100 hover:bg-secondary-50 dark:hover:bg-secondary-500 rounded-md transition',
        day_selected:
          'bg-primary-500 text-neutral-default focus:bg-primary-500',
        day_today:
          ' text-primary-500 text-neutral-default aria-selected:text-white font-semibold',
        day_outside: 'text-secondary-200',
        day_disabled: 'text-secondary-200',
        day_range_start: 'rounded-s-md',
        day_range_end: 'rounded-e-md',
        day_range_middle:
          'aria-selected:bg-secondary-50 dark:bg-secondary-600 aria-selected:text-text-default',
        day_hidden: 'invisible',
        vhidden: 'hidden',
        ...classNames,
      }}
      {...props}
    />
  )
}

const Dropdown = ({ name, value, onChange, children }: DropdownProps) => {
  const options: SelectOptionsType = []

  Children.map(
    children as any,
    (child) =>
      child &&
      options.push({
        value: String(child.props.value),
        label: child.props.children,
      }),
  )

  return (
    <Select
      name={name}
      value={String(value)}
      onValueChange={(value) => onChange?.({ target: { value, name } } as any)}
    >
      <SelectTrigger className="text-secondary-500 w-full flex-1 justify-center gap-1 text-base [&>div]:flex-none">
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="max-h-[254px]">
        {options.map(({ label, value }) => (
          <SelectItem
            className="justify-center"
            key={value}
            value={value}
          >
            {label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
